import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  Grid,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Typography,
} from "@mui/material";
import { FaEye, FaSpinner, FaTrash, FaExchangeAlt } from "react-icons/fa";
import TransferWorkDialog from "./models/transfer";
import OrderDetailsDialog from "./models/order_details";
import StatCard from "../components/stats";
import { jwtDecode } from "jwt-decode";
import { DASHBOARD_URL, DELETE_ORDER_URL, GET_ALL_USERS_URL, GET_ORDER_URL, formatToIndianCurrency } from "../utils";

const Orders = () => {
  const decoded = jwtDecode(localStorage.getItem("token"));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [openModal, setOpenModal] = useState(false);
  const [openTransModal, setOpenTransModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isStatsCalled, setStatsCalled] = useState(false);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    completedOrders: 0,
    newOrders: 0,
    pendingOrders: 0,
    acceptedOrders: 0,
  });
  const [users, setUsers] = useState([]);
  // filter
  const [filter, setFilter] = useState({
    paymentStatus: "",
    status: "New Order",
    owner: "",
  });

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getFilterQueryString = (filter) => {
    const queryParams = new URLSearchParams();

    for (const key in filter) {
      if (filter[key] !== "") {
        queryParams.append(key, filter[key]);
      }
    }

    return queryParams.toString(); // Returns a query string like "paymentStatus=Paid&status=Completed"
  };

  useEffect(() => {
    fetchOrders(filter);
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchOrders(filter);
  }, [filter]);
  useEffect(() => {
    if (!isStatsCalled) {
      fetchDashboardStats();
    }
  }, [isStatsCalled]);

  const fetchOrders = async (filter) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const queryString = getFilterQueryString(filter);
      const apiUrl = `${GET_ORDER_URL}?${queryString}`;
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `${token}` },
      });
      setOrders(response?.data?.orders);
    } catch (error) {
      checkTokenAndRedirect(error);
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(GET_ALL_USERS_URL, {
        headers: { Authorization: `${token}` },
      });
      setUsers(response?.data?.users);
    } catch (error) {
      checkTokenAndRedirect(error);
      console.error("Error fetching users:", error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = DASHBOARD_URL;
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `${token}` },
      });
      // Set the stats to state
      setStats(response?.data);
      setStatsCalled(true);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (order) => {
    setSelectedOrder(order);
    setOpenModal(true);
  };

  const handleCloseModal = (_, flag = false) => {
    setOpenModal(false);
    setSelectedOrder(null);
    if (flag) {
      fetchOrders(filter);
    }
  };

  // Delete order with confirmation
  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this order? This action cannot be undone."
    );
    if (isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${DELETE_ORDER_URL}?orderId=${id}`, {
          headers: { Authorization: `${token}` },
        });
        fetchOrders(filter); // Refresh the order list after deletion
      } catch (error) {
        console.error("Error deleting order:", error);
      }
    }
  };

  const checkTokenAndRedirect = (error) => {
    const tokenErrorMessages = ["Invalid Token", "Token is invalid"]; // Add any specific messages you expect
    const statusCode = error?.status;

    // Check for 403 status code or specific error messages in error
    if (
      statusCode === 403 ||
      (error.response?.data &&
        tokenErrorMessages.some((msg) =>
          error.response?.data?.message.includes(msg)
        ))
    ) {
      // Delete the token from local storage
      localStorage.removeItem("token");

      // Redirect to the login page
      window.location.href = "/login"; // Adjust the URL according to your routing setup
    }
  };

  const handleTransfer = (order) => {
    setSelectedOrder(order);
    setOpenTransModal(true);
  };

  // Define color mapping for statuses
  const paymentStatusColors = {
    Pending: "warning",
    "Advance Paid": "info",
    Paid: "success",
  };

  const orderStatusColors = {
    "New Order": "info",
    "In Progress": "warning",
    Rejected: "error",
    Completed: "success",
    Accepted: "secondary",
  };

  const statsData = [
    {
      title: "Completed Orders",
      value: stats.completedOrders,
      color: "#4CAF50",
    },
    { title: "Pending Orders", value: stats.pendingOrders, color: "#FFC107" },
    {
      title: "Accepted/New Orders",
      value: `${stats.newOrders}/${stats.acceptedOrders}`,
      color: "#F44336",
    },
    {
      title: `${months[new Date().getMonth()]}` + "'s Earning",
      value: "₹ " + formatToIndianCurrency(decoded.role === "admin" ? stats.totalRevenue : stats.totalRevenue * 0.5),
      color: "#2196F3",
    },
  ];

  return (
    <div style={{ padding: "2rem" }}>
      <Grid container spacing={6} style={{ marginBottom: 24 }}>
        {statsData?.map((stat) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            color={stat.color}
          />
        ))}
      </Grid>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter.status}
              label="Status"
              defaultValue="New Order"
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            >
              <MenuItem value="New Order">New Order</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Accepted">Accepted</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={filter.paymentStatus}
              label="Payment Status"
              onChange={(e) =>
                setFilter({ ...filter, paymentStatus: e.target.value })
              }
            >
            <MenuItem value="">All</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Advance Paid">Advance Paid</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {decoded.role === "admin" && (
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Owner</InputLabel>
              <Select
                value={filter.owner}
                label="Assign to"
                onChange={(e) =>
                  setFilter({ ...filter, owner: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                {users?.map((user) => (
                  <MenuItem value={user?.id}>{user.full_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            variant="text"
            color="secondary"
            onClick={() =>
              setFilter({ status: "", paymentStatus: "", owner: "" })
            }
          >
            Clear Filter
          </Button>
        </Grid>
      </Grid>
      <TableContainer>
        <Table>
          <TableHead style={{ backgroundColor: "#BA9F52" }}>
            <TableRow>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Tracking ID
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Order Date
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Bride & Groom Names
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Status
              </TableCell>
              {decoded.role === "admin" && (
                <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                  Amount Received
                </TableCell>
              )}
              {decoded.role === "admin" && (
                <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                  Order Amount
                </TableCell>
              )}
              {decoded.role !== "admin" && (
                <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                  No of events
                </TableCell>
              )}
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Payment Status
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Contact No
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Assigned to
              </TableCell>
              <TableCell style={{ color: "#FFF", fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow key={order.tracking_id}>
                  <TableCell>{order.tracking_id?.split("TRK-")[1]}</TableCell>
                  <TableCell>{order.created_at?.slice(0,10)}</TableCell>
                  <TableCell>{`${order.bride_name} & ${order.groom_name}`}</TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      color={orderStatusColors[order.status]}
                    />
                  </TableCell>
                  {decoded.role === "admin" && (
                    <TableCell
                      style={{
                        color: order.amount_received > 0 ? "green" : "red",
                      }}
                    >{`₹ ${formatToIndianCurrency(order.amount_received)}`}</TableCell>
                  )}
                  {decoded.role === "admin" && (
                    <TableCell>{`₹ ${formatToIndianCurrency(order.order_amount)}`}</TableCell>
                  )}
                  {decoded.role !== "admin" && (
                    <TableCell>{`${order.no_of_event}`}</TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={order.payment_status}
                      color={
                        paymentStatusColors[order.payment_status] || "default"
                      }
                    />
                  </TableCell>
                  <TableCell>{order.contact_no}</TableCell>
                  <TableCell>{order.owner_name}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleView(order)}>
                      <FaEye />
                    </IconButton>
                    {decoded.role === "admin" && <IconButton onClick={() => handleDelete(order.id)}>
                      <FaTrash />
                    </IconButton>}
                    <IconButton onClick={() => handleTransfer(order)}>
                      <FaExchangeAlt />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {!loading && orders.length === 0 && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="red" style={{ marginLeft: "45vw" }}>
                    No Data Found
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {loading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={9}>
                  <div style={{ textAlign: "center", padding: "2rem" }}>
                    <FaSpinner className="fa-spin" />
                    <p>Loading orders...</p>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={orders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Order Details Modal */}
      <OrderDetailsDialog
        open={openModal}
        onClose={handleCloseModal}
        selectedOrder={selectedOrder}
        onMarkPaidAdvance={() => console.log("Mark as Paid Advance")}
        onMarkFullPaid={() => console.log("Mark as Full Paid")}
        onAccept={() => console.log("Accepted")}
      />

      <TransferWorkDialog
        names={users}
        order={selectedOrder}
        open={openTransModal}
        fetchOrders={fetchOrders}
        onClose={() => setOpenTransModal(false)}
      />
    </div>
  );
};

export default Orders;
